import React, { useEffect, useState } from 'react';
import { analytics, ai, scheduler } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Link } from 'react-router-dom';
import { Sparkles, CalendarDays, Search, Rocket, Zap, Mail, BarChart3, Plus, ArrowRight } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      analytics.summary().catch(() => null),
      ai.insights().catch(() => []),
      scheduler.queue().catch(() => []),
    ]).then(([s, ins, q]) => {
      setSummary(s);
      setInsights(Array.isArray(ins) ? ins : []);
      setQueue(Array.isArray(q) ? q : []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;

  const metrics = [
    { label: 'Growth Score', value: summary?.growthScore ?? 0, unit: '/100', color: '#FFD166', bar: (summary?.growthScore ?? 0) },
    { label: 'Posts Published', value: summary?.posts?.published ?? 0, unit: '', color: '#06D6A0', bar: Math.min((summary?.posts?.published ?? 0) * 5, 100) },
    { label: 'Total Reach', value: fmtNum(summary?.reach ?? 0), unit: '', color: '#118AB2', bar: Math.min((summary?.reach ?? 0) / 100, 100) },
    { label: 'Scheduled', value: summary?.posts?.scheduled ?? 0, unit: ' posts', color: '#EF476F', bar: Math.min((summary?.posts?.scheduled ?? 0) * 10, 100) },
  ];

  return (
    <div className="page-enter">
      {/* Topbar */}
      <div style={s.topbar}>
        <div>
          <div style={s.greeting}>Good {timeOfDay()}, {user?.name?.split(' ')[0]} 👋</div>
          <div style={s.subtitle}>Here's how your marketing is performing</div>
        </div>
        <Link to="/attention" style={s.ctaBtn}>
          <Plus size={18} strokeWidth={3} /> Create Post
        </Link>
      </div>

      <div style={s.content}>
        {/* Metric cards */}
        <div style={s.metricsGrid}>
          {metrics.map(m => (
            <div key={m.label} style={{...s.metCard, background: m.color}}>
              <div style={s.metLabel}>{m.label}</div>
              <div style={s.metValue}>
                {m.value}<span style={s.metUnit}>{m.unit}</span>
              </div>
              <div style={s.barTrack}>
                <div style={{...s.barFill, width:`${m.bar}%`}} />
              </div>
            </div>
          ))}
        </div>

        <div style={s.twoCol}>
          {/* AI Insights */}
          <div>
            <div style={s.sectionTitle}><Sparkles size={20} strokeWidth={2.5}/> Mono Insights</div>
            <div style={s.card}>
              {insights.length === 0 && <EmptyState icon={<Search size={40} strokeWidth={2}/>} text="Publish your first post to unlock AI insights" />}
              {insights.map((ins, i) => (
                <div key={i} style={{...s.insightRow, borderBottom: i < insights.length-1 ? '3px solid #222' : 'none'}}>
                  <span style={{...s.insightTag, ...(tagStyle(ins.type))}}>{ins.type}</span>
                  <span style={s.insightText}>{ins.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming queue */}
          <div>
            <div style={s.sectionTitle}><CalendarDays size={20} strokeWidth={2.5}/> Scheduled Queue</div>
            <div style={s.card}>
              {queue.length === 0 && <EmptyState icon={<CalendarDays size={40} strokeWidth={2}/>} text="No posts scheduled yet — go create some!" />}
              {queue.slice(0,5).map(p => (
                <div key={p.id} style={{...s.queueRow, borderBottom:'3px solid #222'}}>
                  <div style={{...s.platformDot, background: platformColor(p.platform)}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={s.queueContent}>{p.content.slice(0,60)}{p.content.length>60?'…':''}</div>
                    <div style={s.queueTime}>{p.platform} · {fmtDate(p.scheduled_at)}</div>
                  </div>
                </div>
              ))}
              {queue.length > 0 && (
                <Link to="/attention" style={s.viewAll}>View all <ArrowRight size={14} strokeWidth={3} style={{marginLeft:4, verticalAlign:'-3px'}}/></Link>
              )}
            </div>
          </div>
        </div>

        {/* Engine grid */}
        <div style={s.sectionTitle}><Zap size={20} strokeWidth={2.5}/> Your Engines</div>
        <div style={s.engineGrid}>
          {engines.map(e => (
            <div key={e.key} style={{...s.engineCard, opacity: e.live ? 1 : 0.6}}>
              <div style={s.engineHeader}>
                <div style={s.engineIcon}>{e.icon}</div>
                {e.live
                  ? <span style={s.liveBadge}>LIVE</span>
                  : <span style={s.soonBadge}>SOON</span>}
              </div>
              <div style={s.engineName}>{e.name}</div>
              <div style={s.engineDesc}>{e.desc}</div>
              {e.live && <Link to={e.path} style={s.engineBtn}>Open Engine <ArrowRight size={14} strokeWidth={3} style={{marginLeft:4, verticalAlign:'-3px'}}/></Link>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:16,background:'#F6F8FA'}}>
      <div className="cham-logo" style={{background:'#FFD166',borderRadius:16,border:'4px solid #222',boxShadow:'4px 4px 0px #222',padding:16}}>
        <Sparkles size={48} color="#222" strokeWidth={3} />
      </div>
      <div style={{fontFamily:'"Fredoka",sans-serif',color:'#222',fontSize:18,fontWeight:600}}>Loading...</div>
    </div>
  );
}
function EmptyState({icon,text}){ return <div style={{padding:'40px 20px',textAlign:'center',color:'#444'}}><div style={{marginBottom:16, color:'#222'}}>{icon}</div><div style={{fontFamily:'"Fredoka",sans-serif',fontWeight:600,fontSize:16}}>{text}</div></div>; }

function timeOfDay(){ const h=new Date().getHours(); return h<12?'morning':h<17?'afternoon':'evening'; }
function fmtNum(n){ return n>=1000?`${(n/1000).toFixed(1)}K`:n; }
function fmtDate(unix){ if(!unix)return''; const d=new Date(unix*1000); return d.toLocaleDateString('en-IN',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); }
function platformColor(p){ return {instagram:'#E1306C',twitter:'#1DA1F2',linkedin:'#0077B5',facebook:'#1877F2'}[p]||'#222'; }
function tagStyle(t){ return {tip:{background:'#06D6A0',color:'#222'},warn:{background:'#FFD166',color:'#222'},win:{background:'#118AB2',color:'#FFF'}}[t]||{background:'#EEE',color:'#222'}; }

const engines = [
  {key:'attention',icon:<Search size={32} strokeWidth={2.5}/>,name:'Attention Engine',desc:'Schedule content, grow your audience',live:true,path:'/attention'},
  {key:'intent',icon:<Search size={32} strokeWidth={2.5}/>,name:'Intent Engine',desc:'Keywords, SEO scoring, organic growth',live:false,path:'/intent'},
  {key:'acquisition',icon:<Rocket size={32} strokeWidth={2.5}/>,name:'Acquisition Engine',desc:'Run ads, track spend, generate leads',live:false,path:'/acquisition'},
  {key:'conversion',icon:<Zap size={32} strokeWidth={2.5}/>,name:'Conversion Engine',desc:'Landing pages, funnels, CRO',live:false,path:'/conversion'},
  {key:'retention',icon:<Mail size={32} strokeWidth={2.5}/>,name:'Retention Engine',desc:'Email campaigns, CRM, churn reduction',live:false,path:'/retention'},
  {key:'optimization',icon:<BarChart3 size={32} strokeWidth={2.5}/>,name:'Optimization Engine',desc:'Unified analytics, AI-powered insights',live:false,path:'/optimization'},
];

const s = {
  topbar:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'24px 32px',borderBottom:'3px solid #222',background:'#FFF'},
  greeting:{fontFamily:'"Fredoka",sans-serif',fontSize:26,fontWeight:700,color:'#222',letterSpacing:'-0.5px'},
  subtitle:{fontFamily:'"Nunito",sans-serif',fontSize:15,color:'#666',marginTop:4,fontWeight:600},
  ctaBtn:{display:'flex',alignItems:'center',gap:8,background:'#FF5C5C',border:'3px solid #222',boxShadow:'3px 3px 0px #222',color:'#FFF',padding:'12px 24px',borderRadius:12,textDecoration:'none',fontFamily:'"Fredoka",sans-serif',fontSize:16,fontWeight:700,letterSpacing:'0.02em',transition:'all 0.1s',transform:'translate(0,0)'},
  content:{padding:32},
  metricsGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:20,marginBottom:32},
  metCard:{border:'3px solid #222',boxShadow:'4px 4px 0px #222',borderRadius:16,padding:'24px'},
  metLabel:{fontFamily:'"Fredoka",sans-serif',fontSize:14,fontWeight:800,color:'#222',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:12},
  metValue:{fontFamily:'"Fredoka",sans-serif',fontSize:36,fontWeight:700,color:'#222',letterSpacing:'-1px'},
  metUnit:{fontSize:16,fontWeight:600,fontFamily:'"Nunito",sans-serif'},
  barTrack:{height:8,background:'#FFF',border:'2px solid #222',borderRadius:8,marginTop:16,overflow:'hidden'},
  barFill:{height:'100%',background:'#222',borderRadius:0,transition:'width 0.6s ease'},
  twoCol:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:32},
  sectionTitle:{display:'flex',alignItems:'center',gap:10,fontFamily:'"Fredoka",sans-serif',fontSize:20,fontWeight:700,color:'#222',marginBottom:16},
  card:{background:'#FFF',border:'3px solid #222',boxShadow:'6px 6px 0px #222',borderRadius:16,overflow:'hidden'},
  insightRow:{display:'flex',alignItems:'flex-start',gap:12,padding:'16px 20px',background:'#FFF'},
  insightTag:{fontFamily:'"Fredoka",sans-serif',fontSize:11,padding:'4px 10px',border:'2px solid #222',borderRadius:8,fontWeight:800,letterSpacing:'0.05em',textTransform:'uppercase',flexShrink:0,marginTop:2},
  insightText:{fontFamily:'"Nunito",sans-serif',fontSize:15,color:'#222',lineHeight:1.5,fontWeight:600},
  queueRow:{display:'flex',alignItems:'flex-start',gap:16,padding:'16px 20px',background:'#FFF'},
  platformDot:{width:16,height:16,borderRadius:'50%',flexShrink:0,marginTop:4,border:'2px solid #222',boxShadow:'1px 1px 0px #222'},
  queueContent:{fontFamily:'"Nunito",sans-serif',fontSize:15,color:'#222',fontWeight:600,lineHeight:1.4},
  queueTime:{fontFamily:'"Fredoka",sans-serif',fontSize:13,color:'#666',fontWeight:600,marginTop:6},
  viewAll:{display:'block',textAlign:'center',padding:'14px',background:'#F6F8FA',color:'#222',textDecoration:'none',fontFamily:'"Fredoka",sans-serif',fontSize:15,fontWeight:700,borderTop:'3px solid #222',transition:'all 0.1s'},
  engineGrid:{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:20},
  engineCard:{background:'#FFF',border:'3px solid #222',boxShadow:'4px 4px 0px #222',borderRadius:16,padding:'24px',transition:'transform 0.1s, box-shadow 0.1s',cursor:'pointer'},
  engineHeader:{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16},
  engineIcon:{background:'#FFD166',border:'2px solid #222',borderRadius:12,width:48,height:48,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'2px 2px 0px #222'},
  liveBadge:{fontFamily:'"Fredoka",sans-serif',fontSize:11,background:'#06D6A0',border:'2px solid #222',color:'#222',padding:'4px 10px',borderRadius:20,fontWeight:800,boxShadow:'2px 2px 0px #222'},
  soonBadge:{fontFamily:'"Fredoka",sans-serif',fontSize:11,background:'#EEE',border:'2px solid #222',color:'#666',padding:'4px 10px',borderRadius:20,fontWeight:800},
  engineName:{fontFamily:'"Fredoka",sans-serif',fontSize:18,fontWeight:700,color:'#222',marginBottom:8},
  engineDesc:{fontFamily:'"Nunito",sans-serif',fontSize:14,color:'#444',lineHeight:1.5,marginBottom:20,fontWeight:600},
  engineBtn:{display:'inline-flex',alignItems:'center',background:'#FFD166',border:'2px solid #222',color:'#222',textDecoration:'none',fontFamily:'"Fredoka",sans-serif',fontSize:14,fontWeight:700,padding:'8px 16px',borderRadius:8,boxShadow:'2px 2px 0px #222',transition:'all 0.1s'},
};
